import type { PlaceRef, UserPreferences } from '@my-fullstack-app/itinerary-types';
import { trpc } from '../lib/trpc';

export interface ResolvedPreferences {
  preferredCurrency: string;
  temperatureUnit: 'celsius' | 'fahrenheit';
  timeFormat: '12h' | '24h';
  distanceUnit: 'km' | 'mi';
  /** ISO 3166-1 alpha-2 — empty string when the user hasn't set one.
   *  Empty (not 'US') is the unset sentinel so UI can branch on
   *  "personalize-or-prompt" without inventing a fake default. */
  nationality: string;
  /** Picked-place snapshot for the user's home base (ADR-0054).
   *  `null` when the user hasn't picked one. */
  home: PlaceRef | null;
}

const DEFAULTS: ResolvedPreferences = {
  preferredCurrency: 'USD',
  temperatureUnit: 'celsius',
  timeFormat: '12h',
  distanceUnit: 'km',
  nationality: '',
  home: null,
};

/**
 * Single read path for the display-affecting user preferences. React
 * Query dedupes by query key, so calling this from many leaf
 * components is cheap. Returns sane defaults while the query is
 * loading or when the user has not set a value yet.
 */
export function usePreferences(): ResolvedPreferences {
  const q = trpc.userPrefs.get.useQuery(undefined, { staleTime: Infinity });
  const p = q.data as UserPreferences | undefined;
  const cur = p?.preferredCurrency?.trim().toUpperCase();
  return {
    preferredCurrency: cur || DEFAULTS.preferredCurrency,
    temperatureUnit: p?.temperatureUnit ?? DEFAULTS.temperatureUnit,
    timeFormat: p?.timeFormat ?? DEFAULTS.timeFormat,
    distanceUnit: p?.distanceUnit ?? DEFAULTS.distanceUnit,
    nationality: (p?.nationality?.trim().toUpperCase()) || DEFAULTS.nationality,
    home: p?.home ?? DEFAULTS.home,
  };
}
